import {
  type Horarios,
  type TipoDeLocal,
  DIAS_DA_SEMANA,
  formatarCep,
  ROTULO_DO_DIA,
  ROTULO_DO_TIPO_DE_LOCAL,
  TIPOS_DE_LOCAL,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Building2,
  Car,
  CircleCheck,
  CircleSlash,
  Clock,
  EllipsisVertical,
  LoaderCircle,
  MapPin,
  MapPinned,
  Pencil,
  Plane,
  Power,
  Search,
  Store,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  type FiltroDeAtivo,
  type Local,
  useAtualizarLocal,
  useDesativarLocal,
  useLocais,
} from '@/features/frota/api'
import { DialogoDeLocal } from '@/features/frota/dialogo-de-local'
import { errorMessage } from '@/lib/api'
import { SEM_VALOR } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { useValorAtrasado } from '@/lib/hooks'
import { usePermissao } from '@/lib/permissoes'

/**
 * Pontos de retirada e devolução.
 *
 * ---------------------------------------------------------------------------
 * "FECHADO NOS SETE DIAS" É UM ESTADO PERIGOSO, ENTÃO ELE APARECE NA LISTA
 * ---------------------------------------------------------------------------
 * A grade de horários guarda uma lista de faixas por dia, e dia sem faixa é
 * FECHADO. Um ponto cadastrado às pressas nasce fechado a semana inteira — o
 * dado está certo, mas é quase sempre esquecimento. Por isso a coluna de
 * atendimento não mostra só "0 dias": mostra um selo de aviso, porque a
 * consequência (o ponto não aparece para retirada no app) é invisível daqui.
 *
 * ---------------------------------------------------------------------------
 * DESATIVAR UM PONTO TEM UM EFEITO COLATERAL QUE NÃO SE VÊ
 * ---------------------------------------------------------------------------
 * Desativar NÃO apaga: reserva, contrato e vistoria continuam apontando para
 * onde o carro foi retirado. O que muda é que os veículos baseados ali ficam
 * com uma base que parou de operar. Esse número é mostrado ANTES (na confirmação,
 * a partir da contagem que a linha já traz) e DEPOIS (no aviso de sucesso, com o
 * número que o servidor devolveu) — decisão de gestão não se toma às cegas.
 */

const TAMANHO_DA_PAGINA = 20

/**
 * Ícone por tipo de ponto. O selo é `outline`, nunca `brand`: tipo de local
 * aparece em toda linha, e pintá-lo de amarelo deixaria a tela amarela — que é
 * exatamente o que o CLAUDE.md proíbe. O peso visual vem do ícone.
 */
const ICONE_DO_TIPO = {
  SEDE: Building2,
  FILIAL: Store,
  AEROPORTO: Plane,
  PONTO_DE_ENTREGA: MapPin,
} as const satisfies Readonly<Record<TipoDeLocal, unknown>>

/** Os dois estados do filtro booleano. `''` (todos) não entra na lista. */
const OPCOES_DE_ATIVO = ['true', 'false'] as const satisfies readonly FiltroDeAtivo[]

const ROTULO_DE_ATIVO: Readonly<Record<'true' | 'false', string>> = {
  true: 'Ativos',
  false: 'Desativados',
}

function BadgeDeTipoDeLocal({ tipo }: { readonly tipo: TipoDeLocal }): ReactElement {
  const Icone = ICONE_DO_TIPO[tipo]

  return (
    <Badge variant="outline">
      <Icone aria-hidden="true" />
      {ROTULO_DO_TIPO_DE_LOCAL[tipo]}
    </Badge>
  )
}

/**
 * Situação do ponto. Verde/cinza + ícone + rótulo: desativado não é erro (é uma
 * decisão), então não é vermelho — mas também não pode depender só da cor.
 */
function BadgeDeSituacao({ ativo }: { readonly ativo: boolean }): ReactElement {
  return ativo ? (
    <Badge variant="success">
      <CircleCheck aria-hidden="true" />
      Ativo
    </Badge>
  ) : (
    <Badge variant="neutral">
      <CircleSlash aria-hidden="true" />
      Desativado
    </Badge>
  )
}

function quantidadeDeDiasAbertos(horarios: Horarios): number {
  return DIAS_DA_SEMANA.filter((dia) => horarios[dia].length > 0).length
}

/** A grade inteira em texto, para o `title` da célula — sete linhas, sem abreviar. */
function gradeEmTexto(horarios: Horarios): string {
  return DIAS_DA_SEMANA.map((dia) => {
    const faixas = horarios[dia]
    const texto =
      faixas.length === 0
        ? 'fechado'
        : faixas.map((faixa) => `${faixa.abre}–${faixa.fecha}`).join(', ')

    return `${ROTULO_DO_DIA[dia]}: ${texto}`
  }).join('\n')
}

interface DialogoDeDesativacaoProps {
  /** `null` mantém fechado. */
  readonly local: Local | null
  readonly aoFechar: () => void
}

/**
 * Confirmação da desativação.
 *
 * Existe porque desativar um ponto some com ele do seletor de retirada do app —
 * um clique errado tira uma base do ar sem nenhum sinal na tela. O diálogo diz o
 * que NÃO acontece (nada é apagado) e o que acontece (N carros ficam com a base
 * parada), usando a contagem que a própria linha já carrega.
 */
function DialogoDeDesativacao({ local, aoFechar }: DialogoDeDesativacaoProps): ReactElement {
  const desativar = useDesativarLocal()
  const [erro, setErro] = useState<string | null>(null)
  const ocupado = desativar.isPending

  // O diálogo é montado uma vez e reaproveitado por todos os pontos da lista:
  // sem esta limpeza, a falha ao desativar o ponto A apareceria na confirmação
  // do ponto B, e a pessoa desistiria de uma operação que ia funcionar.
  useEffect(() => {
    setErro(null)
  }, [local])

  function confirmar(): void {
    if (local === null) {
      return
    }

    setErro(null)

    desativar.mutate(local.id, {
      onSuccess: (resultado) => {
        toast.success(`${local.nome} foi desativado`, {
          description:
            resultado.veiculosSemBase > 0
              ? `${String(resultado.veiculosSemBase)} ${
                  resultado.veiculosSemBase === 1 ? 'veículo continua' : 'veículos continuam'
                } com este ponto como base — remaneje a frota.`
              : 'Nenhum veículo estava baseado aqui.',
        })
        aoFechar()
      },
      // O erro fica NO diálogo, não num toast: o diálogo continua aberto depois
      // da falha, e um aviso que some sozinho deixaria a tela dizendo apenas
      // "Desativar ponto?" de novo, como se nada tivesse acontecido.
      onError: (causa: unknown) => {
        setErro(errorMessage(causa))
      },
    })
  }

  return (
    <Dialog
      open={local !== null}
      onOpenChange={(proximo) => {
        if (!proximo && !ocupado) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Desativar {local?.nome ?? 'ponto'}?</DialogTitle>
          <DialogDescription>
            O ponto sai do seletor de retirada do app e deixa de aceitar novas reservas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm text-fg-secondary">
          {erro === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          <p>
            Nada é apagado: reservas, contratos e vistorias continuam apontando para onde o carro
            foi retirado. Reativar depois é um clique na mesma lista.
          </p>

          {local !== null && local.veiculos !== null && local.veiculos > 0 ? (
            <Alert variant="warning">
              <AlertDescription>
                <span className="tabular-money font-semibold">{local.veiculos}</span>{' '}
                {local.veiculos === 1
                  ? 'veículo ainda tem este ponto como base e vai continuar apontando'
                  : 'veículos ainda têm este ponto como base e vão continuar apontando'}{' '}
                para uma base que parou de operar. Remaneje a frota depois de desativar.
              </AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="button" variant="destructive" onClick={confirmar} disabled={ocupado}>
            {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            Desativar ponto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function LocaisPage(): ReactElement {
  const podeCriar = usePermissao('location.create')
  const podeEditar = usePermissao('location.update')
  const podeDesativar = usePermissao('location.delete')

  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState('')
  const [tipo, setTipo] = useState<TipoDeLocal | ''>('')
  const [ativo, setAtivo] = useState<FiltroDeAtivo>('')
  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Local | undefined>(undefined)
  const [localASerDesativado, setLocalASerDesativado] = useState<Local | null>(null)

  const busca = useValorAtrasado(buscaDigitada)

  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, busca, tipo, ativo }),
    [pagina, busca, tipo, ativo],
  )

  const consulta = useLocais(filtros)
  const atualizar = useAtualizarLocal()
  const reativando = atualizar.isPending

  const temFiltro = busca.length > 0 || tipo !== '' || ativo !== ''

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirEdicao(local: Local): void {
    setEmEdicao(local)
    setDialogoAberto(true)
  }

  /**
   * Reativar é um PATCH com `ativo: true` — não há rota própria, porque a
   * desativação não apagou nada. Não pede confirmação: é a ação que devolve
   * capacidade, não a que tira.
   */
  function reativar(local: Local): void {
    atualizar.mutate(
      { id: local.id, dados: { ativo: true } },
      {
        onSuccess: () => {
          toast.success(`${local.nome} voltou a operar`, {
            description: 'O ponto aparece de novo no seletor de retirada.',
          })
        },
        onError: (causa: unknown) => {
          toast.error('Não foi possível reativar o ponto', { description: errorMessage(causa) })
        },
      },
    )
  }

  const colunas = useMemo<ColumnDef<Local>[]>(
    () => [
      {
        id: 'nome',
        header: 'Ponto',
        accessorFn: (linha: Local) => linha.nome,
        cell: ({ row }) => {
          const local: Local = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{local.nome}</p>
              <p className="truncate text-xs text-fg-secondary">
                {local.logradouro}, {local.numero} · {local.bairro}
              </p>
            </div>
          )
        },
      },
      {
        id: 'tipo',
        header: 'Tipo',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeTipoDeLocal tipo={row.original.tipo} />,
      },
      {
        id: 'cidade',
        header: 'Cidade',
        accessorFn: (linha: Local) => `${linha.cidade}/${linha.uf}`,
        cell: ({ row }) => {
          const local: Local = row.original

          return (
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground">
                {local.cidade}/{local.uf}
              </p>
              <p className="truncate text-xs tabular-money text-fg-secondary">
                {formatarCep(local.cep)}
              </p>
            </div>
          )
        },
      },
      {
        id: 'atendimento',
        header: 'Atendimento',
        enableSorting: false,
        cell: ({ row }) => {
          const local: Local = row.original
          const dias = quantidadeDeDiasAbertos(local.horarios)
          const detalhe = gradeEmTexto(local.horarios)

          // Zero dia não é "sem informação": é fechado a semana inteira, e o
          // ponto simplesmente não aparece para retirada. Selo de aviso (âmbar,
          // com ícone), nunca o amarelo da marca.
          if (dias === 0) {
            return (
              <Badge variant="warning" title={detalhe}>
                <TriangleAlert aria-hidden="true" />
                Fechado nos 7 dias
              </Badge>
            )
          }

          return (
            <span
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-fg-secondary"
              title={detalhe}
            >
              <Clock className="size-3.5 text-fg-muted" aria-hidden="true" />
              <span className="tabular-money">{dias}</span>
              {dias === 1 ? ' dia por semana' : ' dias por semana'}
            </span>
          )
        },
      },
      {
        id: 'taxas',
        header: 'Taxas',
        // Ordenar por dinheiro exigiria comparar a string de centavos, que
        // ordena por texto ("9" > "1000"). Sem número, sem ordenação.
        enableSorting: false,
        cell: ({ row }) => {
          const local: Local = row.original

          return (
            <div className="min-w-0 whitespace-nowrap">
              <p className="text-sm tabular-money text-foreground">
                Entrega {formatarMoedaDoWire(local.taxaDeEntrega)}
              </p>
              <p className="text-xs tabular-money text-fg-secondary">
                Devolução {formatarMoedaDoWire(local.taxaDeDevolucao)}
              </p>
            </div>
          )
        },
      },
      {
        id: 'veiculos',
        header: 'Frota',
        accessorFn: (linha: Local) => linha.veiculos,
        cell: ({ row }) => {
          const local: Local = row.original

          return (
            <span
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-fg-secondary"
              title="Veículos que têm este ponto como base"
            >
              <Car className="size-3.5 text-fg-muted" aria-hidden="true" />
              <span className="tabular-money">{local.veiculos ?? '—'}</span>
            </span>
          )
        },
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => {
          const local: Local = row.original

          return (
            <div className="flex flex-wrap items-center gap-1.5">
              <BadgeDeSituacao ativo={local.ativo} />
              {!local.ativo && local.veiculos !== null && local.veiculos > 0 ? (
                <Badge
                  variant="warning"
                  title="A base parou de operar, mas os veículos continuam apontando para ela"
                >
                  <TriangleAlert aria-hidden="true" />
                  <span className="tabular-money">{local.veiculos}</span> sem base
                </Badge>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const local: Local = row.original

          // A ação disponível depende da SITUAÇÃO da linha, não só da permissão:
          // reativar só existe em ponto desativado, desativar só em ponto no ar.
          // Sem contar as duas coisas juntas, quem tivesse `location.delete` sem
          // `location.update` abriria um menu VAZIO em todo ponto já desativado —
          // um botão que promete uma ação que não existe.
          const podeReativar = podeEditar && !local.ativo
          const podeTirarDoAr = podeDesativar && local.ativo

          if (!podeEditar && !podeTirarDoAr) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações de ${local.nome}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                {podeEditar ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      abrirEdicao(local)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Editar ponto
                  </DropdownMenuItem>
                ) : null}

                {podeReativar ? (
                  <DropdownMenuItem
                    // Uma reativação por vez: o item fica no menu enquanto o
                    // PATCH corre, e sem isto o segundo clique dispararia outra
                    // requisição e outro toast dizendo a mesma coisa.
                    disabled={reativando}
                    onSelect={() => {
                      reativar(local)
                    }}
                  >
                    <Power aria-hidden="true" />
                    Reativar ponto
                  </DropdownMenuItem>
                ) : null}

                {podeTirarDoAr ? (
                  <>
                    {podeEditar ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      onSelect={() => {
                        setLocalASerDesativado(local)
                      }}
                    >
                      <CircleSlash aria-hidden="true" />
                      Desativar ponto
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas são as permissões e a reativação em curso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeDesativar, reativando],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Pontos de atendimento</h1>
          <p className="text-sm text-fg-secondary">
            Onde o cliente retira e devolve o carro. O horário de cada ponto é o que limita as
            janelas de retirada no app.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirCriacao}>
            <MapPinned aria-hidden="true" />
            Novo ponto
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="filtro-busca-locais">Buscar</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                text-fg-muted"
            />
            <Input
              id="filtro-busca-locais"
              type="search"
              value={buscaDigitada}
              onChange={(evento) => {
                setPagina(1)
                setBuscaDigitada(evento.target.value)
              }}
              placeholder="Nome, endereço, cidade ou CEP"
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-tipo-local">Tipo</Label>
          <Select
            id="filtro-tipo-local"
            value={tipo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setTipo(TIPOS_DE_LOCAL.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {TIPOS_DE_LOCAL.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_TIPO_DE_LOCAL[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-ativo-local">Situação</Label>
          <Select
            id="filtro-ativo-local"
            value={ativo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setAtivo(OPCOES_DE_ATIVO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            {OPCOES_DE_ATIVO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_ATIVO[item]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Pontos de retirada e devolução"
        estadoVazio={
          <EmptyState
            icone={MapPinned}
            titulo={temFiltro ? 'Nenhum ponto com esses filtros' : 'Nenhum ponto cadastrado'}
            descricao={
              temFiltro
                ? 'Ajuste a busca, o tipo ou a situação para ver outros resultados.'
                : 'Cadastre a primeira base. Sem ponto de retirada não há reserva: é ele que define onde o carro é entregue e em que horário.'
            }
            acao={
              temFiltro ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setBuscaDigitada('')
                    setTipo('')
                    setAtivo('')
                    setPagina(1)
                  }}
                >
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirCriacao}>
                  <MapPinned aria-hidden="true" />
                  Novo ponto
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeLocal
        aberto={dialogoAberto}
        local={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />

      <DialogoDeDesativacao
        local={localASerDesativado}
        aoFechar={() => {
          setLocalASerDesativado(null)
        }}
      />
    </div>
  )
}
